import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { 
  LayoutDashboard, Bookmark, Settings, LogOut, Menu, X, Shield, Zap, FolderOpen
} from 'lucide-react';

const NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testId: 'nav-dashboard' },
  { path: '/projects', label: 'Projects', icon: FolderOpen, testId: 'nav-projects' },
  { path: '/workspace', label: 'Workspace', icon: Bookmark, testId: 'nav-workspace' },
  { path: '/pricing', label: 'Pricing', icon: Zap, testId: 'nav-pricing' },
  { path: '/profile', label: 'Settings', icon: Settings, testId: 'nav-settings' },
];

export const AppLayout = ({ children, maxWidth = 'max-w-5xl' }) => {
  const { signOut, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <div className="min-h-screen">
      {/* Top nav */}
      <header className="border-b border-border/60 sticky top-0 bg-background/95 backdrop-blur-sm z-20">
        <div className={`${maxWidth} mx-auto px-4 sm:px-6`}>
          <div className="flex items-center justify-between h-14">
            {/* Left: Logo */}
            <Link to="/dashboard" className="flex items-center gap-2 transition-opacity hover:opacity-80" data-testid="nav-brand">
              <img src="/banner-logo.png" alt="Creataby" className="h-12" />
            </Link>

            {/* Center: Desktop nav */}
            <nav className="hidden md:flex items-center gap-1">
              {NAV_ITEMS.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link key={item.path} to={item.path}>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`text-sm rounded-md transition-all ${
                        isActive
                          ? 'bg-teal-50 text-teal-700 font-semibold border-b-2 border-teal-600 rounded-b-none'
                          : 'text-muted-foreground hover:bg-slate-100 hover:text-foreground'
                      }`}
                      data-testid={item.testId}
                    >
                      <item.icon className="h-4 w-4 mr-1.5" />
                      {item.label}
                    </Button>
                  </Link>
                );
              })}
              {isAdmin && (
                <Link to="/admin">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`text-sm rounded-md transition-all ${
                      location.pathname === '/admin'
                        ? 'bg-teal-50 text-teal-700 font-semibold border-b-2 border-teal-600 rounded-b-none'
                        : 'text-muted-foreground hover:bg-slate-100 hover:text-foreground'
                    }`}
                    data-testid="nav-admin"
                  >
                    <Shield className="h-4 w-4 mr-1.5" />
                    Admin
                  </Button>
                </Link>
              )}
            </nav>

            {/* Right: Sign out + Mobile menu toggle */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSignOut}
                className="hidden md:inline-flex text-muted-foreground hover:text-foreground hover:bg-slate-100 text-sm transition-all"
                data-testid="nav-signout"
              >
                <LogOut className="h-4 w-4 mr-1.5" />
                Sign Out
              </Button>

              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-border/60 py-3 space-y-1">
              {NAV_ITEMS.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                      isActive ? 'bg-teal-50 text-teal-700 font-semibold' : 'text-muted-foreground hover:bg-slate-100'
                    }`}>
                      <item.icon className="h-4 w-4" />
                      {item.label}
                    </div>
                  </Link>
                );
              })}
              {isAdmin && (
                <Link to="/admin" onClick={() => setMobileMenuOpen(false)}>
                  <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    location.pathname === '/admin' ? 'bg-teal-50 text-teal-700 font-semibold' : 'text-muted-foreground hover:bg-slate-100'
                  }`}>
                    <Shield className="h-4 w-4" />
                    Admin
                  </div>
                </Link>
              )}
              <button
                onClick={handleSignOut}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground w-full hover:bg-slate-100 transition-colors"
                data-testid="mobile-signout"
              >
                <LogOut className="h-4 w-4" />
                Sign Out
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Page content */}
      <main className={`${maxWidth} mx-auto px-4 sm:px-6 py-6 sm:py-8`}>
        {children}
      </main>
    </div>
  );
};
